import * as Dialog from '@radix-ui/react-dialog';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { ErrorNotice } from '../../components/ErrorNotice';
import { SkeletonText } from '../../components/Skeleton';
import { formatBytes, STATUS_TONES } from './presentation';
import { useBackupDevice, useBackupJob, useRestartBackup } from './queries';

/**
 * Die Geraeteakte -- F5-01, Anforderung 2 und 3.
 *
 * **Beide Quellen in einer Liste.** Die Ereignisse aus der
 * Benachrichtigungsmail und die aus der Anbieterschnittstelle stehen
 * untereinander und tragen ihre Herkunft. Zwei getrennte Listen ueberliessen
 * dem Leser die Frage, welche die richtige ist -- und das ist genau die
 * Frage, die er an dieses System stellt.
 *
 * **"Erneut sichern" meldet auch dann etwas, wenn es scheitert.** Der Auftrag
 * entsteht im System, bevor der Anbieter gefragt wird; lehnt er ab, steht der
 * Grund in der Zeile. Ein Knopf, der bei Fehler 13501 nichts sagt, ist
 * schlimmer als keiner.
 */
/**
 * Wie das Geraet zu nennen ist -- Rechnername, sonst Kontoname.
 *
 * Beide Felder sind Zeichenketten und leer statt ``null``: Das Datenmodell
 * kennt keinen "unbekannten Rechnernamen", nur einen leeren. Deshalb wird auf
 * "leer" geprueft und nicht auf ``null``.
 */
function deviceName(device: { computer_name: string; account_name: string } | undefined) {
  if (device === undefined) return undefined;
  return device.computer_name !== '' ? device.computer_name : device.account_name || undefined;
}

export function BackupDeviceDialog({
  deviceId,
  mayManage,
  onClose,
}: {
  deviceId: string;
  mayManage: boolean;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const device = useBackupDevice(deviceId);
  const restart = useRestartBackup();

  // Der zuletzt ausgeloeste Auftrag wird nachgefuehrt, solange er laeuft --
  // siehe ``useBackupJob``. Danach hoert die Abfrage von selbst auf.
  const [watchedJob, setWatchedJob] = useState<string | null>(null);
  const job = useBackupJob(watchedJob);

  const zeit = (value: string | null | undefined) =>
    value ? new Date(value).toLocaleString(i18n.language) : t('app.none');

  return (
    <Dialog.Root
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="dialog__overlay" />
        <Dialog.Content className="card dialog__content">
          <Dialog.Title asChild>
            <h2 style={{ marginBottom: 'var(--space-4)' }}>
              {deviceName(device.data) ?? t('backup.device')}
            </h2>
          </Dialog.Title>
          <Dialog.Description className="visually-hidden">
            {t('backup.deviceSubtitle')}
          </Dialog.Description>

          {device.isPending ? <SkeletonText lines={5} /> : null}
          {device.isError ? <ErrorNotice error={device.error} /> : null}

          {device.data ? (
            <div className="stack">
              <dl className="definition-list">
                <dt>{t('backup.status')}</dt>
                <dd>
                  <Badge tone={STATUS_TONES[device.data.last_status]}>
                    {t(`backupStatus.${device.data.last_status}`)}
                  </Badge>{' '}
                  {t('backup.errorCountValue', { count: device.data.error_count })}
                </dd>

                <dt>{t('backup.customer')}</dt>
                <dd>{device.data.partner_name || t('app.none')}</dd>

                <dt>{t('backup.accountName')}</dt>
                <dd className="table__muted">{device.data.account_name || t('app.none')}</dd>

                <dt>{t('backup.lastSuccess')}</dt>
                <dd>
                  {device.data.last_success_at
                    ? t('backup.daysAgo', {
                        count: device.data.days_since_success ?? 0,
                        date: new Date(device.data.last_success_at).toLocaleDateString(
                          i18n.language,
                        ),
                      })
                    : t('backup.never')}
                </dd>

                <dt>{t('backup.dataSources')}</dt>
                <dd>{device.data.data_sources.join(', ') || t('app.none')}</dd>

                <dt>{t('backup.storage')}</dt>
                <dd>
                  {formatBytes(device.data.used_storage_bytes, i18n.language) ?? t('app.none')}
                </dd>

                <dt>{t('backup.checkedAt')}</dt>
                <dd className="table__muted">{zeit(device.data.checked_at)}</dd>
              </dl>

              {mayManage ? (
                <div className="row">
                  <Button
                    disabled={restart.isPending}
                    onClick={() => {
                      restart.mutate(
                        { deviceId },
                        {
                          onSuccess: (created) => {
                            setWatchedJob(created.id);
                          },
                        },
                      );
                    }}
                  >
                    {restart.isPending ? t('backup.restarting') : t('backup.restart')}
                  </Button>
                </div>
              ) : null}

              {restart.isError ? <ErrorNotice error={restart.error} /> : null}

              {/* Der Stand des ausgeloesten Auftrags -- angenommen, laeuft,
                  abgeschlossen oder gescheitert. Bei einem Fehler steht die
                  Meldung des Anbieters dabei und nicht "fehlgeschlagen". */}
              {job.data ? (
                <p>
                  <Badge tone={job.data.status === 'failed' ? 'danger' : 'info'}>
                    {t(`backupJobStatus.${job.data.status}`, { defaultValue: job.data.status })}
                  </Badge>{' '}
                  {job.data.error ? <span className="table__muted">{job.data.error}</span> : null}
                </p>
              ) : null}

              <h3>{t('backup.events')}</h3>
              {device.data.events.length === 0 ? (
                <p className="table__muted">{t('backup.noEvents')}</p>
              ) : (
                <div className="table-wrapper">
                  <table className="table">
                    <thead>
                      <tr>
                        <th scope="col">{t('backup.session')}</th>
                        <th scope="col">{t('backup.dataSource')}</th>
                        <th scope="col">{t('backup.status')}</th>
                        <th scope="col">{t('backup.errorCount')}</th>
                        <th scope="col">{t('backup.source')}</th>
                        <th scope="col">{t('backup.occurrences')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {device.data.events.map((event) => (
                        <tr key={event.id}>
                          <td className="table__muted">{zeit(event.session_at)}</td>
                          <td>{event.data_source || t('app.none')}</td>
                          <td>
                            <Badge tone={STATUS_TONES[event.status]}>
                              {t(`backupStatus.${event.status}`)}
                            </Badge>
                          </td>
                          <td className="table__number">{event.error_count}</td>
                          <td className="table__muted">{t(`backupSource.${event.source}`)}</td>
                          <td className="table__number">{event.occurrences}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {device.data.jobs.length > 0 ? (
                <>
                  <h3>{t('backup.jobs')}</h3>
                  <div className="table-wrapper">
                    <table className="table">
                      <thead>
                        <tr>
                          <th scope="col">{t('backup.triggeredAt')}</th>
                          <th scope="col">{t('backup.status')}</th>
                          <th scope="col">{t('backup.jobError')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {device.data.jobs.map((entry) => (
                          <tr key={entry.id}>
                            <td className="table__muted">{zeit(entry.created_at)}</td>
                            <td>
                              <Badge tone={entry.status === 'failed' ? 'danger' : 'info'}>
                                {t(`backupJobStatus.${entry.status}`, {
                                  defaultValue: entry.status,
                                })}
                              </Badge>
                            </td>
                            <td className="table__muted">{entry.error || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : null}
            </div>
          ) : null}

          <div className="row">
            <Button variant="secondary" onClick={onClose}>
              {t('app.close')}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
